/**
 * React Query hooks for Welcome Titles
 *
 * Provides data fetching and mutation hooks for welcome scene and title management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  welcomeApi,
  type SceneCreateRequest,
  type SceneUpdateRequest,
  type TitleCreateRequest,
  type TitleUpdateRequest,
  type WelcomeConfig,
} from '@/lib/api/welcome';
import type { GenerateTitlesRequest } from '@/features/welcome-titles/types';

// ============================================================================
// Query Keys
// ============================================================================

export const welcomeKeys = {
  all: ['welcome'] as const,
  scenes: () => [...welcomeKeys.all, 'scenes'] as const,
  scene: (id: string) => [...welcomeKeys.scenes(), id] as const,
  titles: (sceneId: string) => [...welcomeKeys.all, 'titles', sceneId] as const,
  config: () => [...welcomeKeys.all, 'config'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

export function useWelcomeScenes(includeDisabled = true) {
  return useQuery({
    queryKey: welcomeKeys.scenes(),
    queryFn: () => welcomeApi.listScenes(includeDisabled),
  });
}

export function useWelcomeScene(sceneId: string) {
  return useQuery({
    queryKey: welcomeKeys.scene(sceneId),
    queryFn: () => welcomeApi.getScene(sceneId),
    enabled: !!sceneId,
  });
}

export function useWelcomeTitles(sceneId: string) {
  return useQuery({
    queryKey: welcomeKeys.titles(sceneId),
    queryFn: () => welcomeApi.listTitles(sceneId),
    enabled: !!sceneId,
  });
}

export function useWelcomeConfig() {
  return useQuery({
    queryKey: welcomeKeys.config(),
    queryFn: () => welcomeApi.getConfig(),
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateScene() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SceneCreateRequest) => welcomeApi.createScene(data),
    onSuccess: (scene) => {
      queryClient.invalidateQueries({ queryKey: welcomeKeys.scenes() });
      toast.success(`场景 "${scene.name}" 已创建`);
    },
    onError: (error: Error) => {
      toast.error(`创建失败: ${error.message}`);
    },
  });
}

export function useUpdateScene() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SceneUpdateRequest }) =>
      welcomeApi.updateScene(id, data),
    onSuccess: (scene) => {
      queryClient.invalidateQueries({ queryKey: welcomeKeys.scenes() });
      queryClient.invalidateQueries({ queryKey: welcomeKeys.scene(scene.id) });
      toast.success(`场景 "${scene.name}" 已更新`);
    },
    onError: (error: Error) => {
      toast.error(`更新失败: ${error.message}`);
    },
  });
}

export function useDeleteScene() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sceneId: string) => welcomeApi.deleteScene(sceneId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: welcomeKeys.scenes() });
      toast.success('场景已删除');
    },
    onError: (error: Error) => {
      toast.error(`删除失败: ${error.message}`);
    },
  });
}

export function useCreateTitle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sceneId, data }: { sceneId: string; data: TitleCreateRequest }) =>
      welcomeApi.createTitle(sceneId, data),
    onSuccess: (_title, variables) => {
      queryClient.invalidateQueries({ queryKey: welcomeKeys.titles(variables.sceneId) });
      toast.success('文案已添加');
    },
    onError: (error: Error) => {
      toast.error(`添加失败: ${error.message}`);
    },
  });
}

export function useUpdateTitle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ titleId, sceneId: _sceneId, data }: { titleId: string; sceneId: string; data: TitleUpdateRequest }) =>
      welcomeApi.updateTitle(titleId, data),
    onSuccess: (_title, variables) => {
      queryClient.invalidateQueries({ queryKey: welcomeKeys.titles(variables.sceneId) });
    },
    onError: (error: Error) => {
      toast.error(`更新失败: ${error.message}`);
    },
  });
}

export function useDeleteTitle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ titleId, sceneId: _sceneId }: { titleId: string; sceneId: string }) =>
      welcomeApi.deleteTitle(titleId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: welcomeKeys.titles(variables.sceneId) });
      toast.success('文案已删除');
    },
    onError: (error: Error) => {
      toast.error(`删除失败: ${error.message}`);
    },
  });
}

export function useUpdateWelcomeConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: Partial<WelcomeConfig>) => welcomeApi.updateConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: welcomeKeys.config() });
      toast.success('配置已保存');
    },
    onError: (error: Error) => {
      toast.error(`保存失败: ${error.message}`);
    },
  });
}

export function useGenerateTitles() {
  return useMutation({
    mutationFn: (data: GenerateTitlesRequest) => welcomeApi.generateTitles(data),
    onError: (error: Error) => { toast.error(`生成失败: ${error.message}`); },
  });
}
